import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ENDPOINTS } from "../../js/api/_config";
import DataTable from "react-data-table-component";
import HidingColumns from "./hidingColumns";
import ExcelExport from "./export-excel";
import PdfExport from "./export-pdf";
import Header from "../common/header/Header";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { customStyles } from "../common/styling/constants";
import ViewModal from "./view-modal";

import { IconSortyBy } from "../../assets/icons/icon-sortby-arrow";
import { IconExportArrow } from "../../assets/icons/icon-export-arrow";
import { IconExportDownload } from "../../assets/icons/icon-export-download";
import { IconViewDetail } from "../../assets/icons/icon-details";
import { IconEdit } from "../../assets/icons/icon-edit";
import { IconViewDetailHovered } from "../../assets/icons/icon-view-detail-hover";
import { IconEditHovered } from "../../assets/icons/icon-edit-hover";
import {
  convertTimeToLocation,
  formatPhoneNumber,
  getLatestMessage,
} from "../common/constant/constant";
import { CustomPagination } from "../common/custom-pagination/custom-pagination";
import NoRecord from "../common/no-record/no-record";
import PatientsNoRecord from "../../assets/icons/patients-no-record";
import Filter from "./filter";
import PrimaryIconButton from "../common/buttons/primary-filter-icon-buttons";
import { trimWithEllipsis } from "./constants";
import useMediaQuery from "../common/custom-hooks/use-media-query";
import { Toast } from "../common/toast/toast";

const Patients = () => {
  let navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isBigDesktop = useMediaQuery("(min-width: 1537px)");
  const filterParam = searchParams.get("filter");
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [search, setSearch] = useState(filterParam);
  const [showClose, setShowClose] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditHovered, setIsEditHovered] = useState(null);
  const [isViewHovered, setIsViewHovered] = useState(null);
  const [patientDetails, setPatientDetails] = useState();
  const [toasts, setToasts] = useState([]);
  const [sortOrder, setSortOrder] = useState(
    searchParams.get("sorting") || "-lastOutreach"
  );
  const [isConversationContactSelected, setIsConversationContactSelected] =
    useState(false);

  const [_patientList, _setPatientList] = useState({
    filter: searchParams.get("filter"),
    columns: "",
    sorts: searchParams.get("sorting") || sortOrder,
    data: [],
    totalCount: 0,
    currentPage: searchParams.get("page")
      ? parseInt(searchParams.get("page")) - 1
      : 0,
    rowsPerPage: searchParams.get("per_page") || 10,
    states: searchParams.get("states"),
    cities: searchParams.get("cities"),
    trialnames: searchParams.get("trialnames"),
    hiddenColumns: [],
    refreshKey: true,
  });

  function shouldReturnTrue(hiddenColumns) {
    const criticalColumns = ["Conversation", "Last Outreach", "Contact"];

    if (!Array.isArray(hiddenColumns)) return false;

    for (const col of criticalColumns) {
      if (!hiddenColumns.includes(col)) {
        return true;
      }
    }

    return false;
  }

  useEffect(() => {
    shouldReturnTrue(_patientList.hiddenColumns)
    setIsConversationContactSelected(
      shouldReturnTrue(_patientList.hiddenColumns)
    );
  }, [_patientList]);

  const dropdownRefExport = useRef({});
  const getPatientById = (patientList, id) => {
    return patientList.find((patient) => patient.patientID === id);
  };

  const onSearchClick = () => {
    _setPatientList({
      ..._patientList,
      //filter: search ? `(firstName|lastName|phoneNumber)@=${search}` : null,
      filter: search,
      currentPage: 0,
    });
    setShowClose(true);
    setIsOpen(false);
  };

  const onCloseClick = () => {
    setSearch("");
    setShowClose(false);
  };

  const fetchData = async () => {
    let queryParams = {
      Page: _patientList.currentPage + 1,
      PageSize: _patientList.rowsPerPage,
      Sorts: _patientList.sorts,
    };

    if (_patientList.filter) {
      queryParams["searchTerm"] = _patientList.filter;
    }

    if (_patientList.cities) {
      queryParams["cities"] = _patientList.cities;
    }

    if (_patientList.states) {
      queryParams["states"] = _patientList.states;
    }

    if (_patientList.trialnames) {
      queryParams["trialnames"] = _patientList.trialnames;
    }

    setIsLoading(true);

    await axios
      .get(ENDPOINTS.PATIENT.PATIENT_LIST, { params: queryParams })
      .then((res) => {
        _setPatientList({
          ..._patientList,
          data: res.data.data.items,
          totalCount: res.data.data.count,
        });

        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  const clearFilter = () => {
    closeModel();
    setHiddenColumns([]);
    //navigate(`${pathname}`, { replace: true });
    _setPatientList({
      ..._patientList,
      filter: null,
      columns: "",
      sorts: sortOrder,
      totalCount: 0,
      currentPage: 0,
      rowsPerPage: 10,
      states: null,
      cities: null,
      trialnames: null,
      hiddenColumns: [],
      refreshKey: !_patientList.refreshKey,
    });
  };

  const toggleFilter = () => {
    setIsOpen(!isOpen);
  };

  const addToast = (message, type) => {
    const id = Date.now();
    setToasts([...toasts, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000); // Auto-dismiss after 3 seconds
  };

  const removeToast = (id) => {
    setToasts(toasts.filter((toast) => toast.id !== id));
  };

  useEffect(() => {
    const handleClickOutside2 = (event) => {
      if (
        dropdownRefExport.current &&
        !dropdownRefExport.current.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("click", handleClickOutside2);
    return () => {
      document.removeEventListener("click", handleClickOutside2);
    };
  }, [isDropdownVisible]);

  const handleSort = async (sortType) => {
    const params = new URLSearchParams(window.location.search);

    setSortOrder(sortType);
    _setPatientList({ ..._patientList, sorts: sortType });

    params.set("sorting", sortType);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const handleToggleDropdown = () => {
    setDropdownVisible((prevState) => !prevState);
  };

  function convertDateLastContracted(isoDateString) {
    const dateObj = new Date(isoDateString);

    const day = dateObj.getDate().toString().padStart(2, "0");
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
    const year = dateObj.getFullYear();

    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");

    const amPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${month}-${day}-${year} ${hours}:${minutes} ${amPm}`;
  }

  function shouldDisableButton(data) {
    const result =
      Array.isArray(data) &&
      data.every((item) => String(item.isPatientDeleted) === "1");
    return result;
  }

  const conditionalRowStyles = [
    {
      when: (row) =>
        Array.isArray(row.patientTrialData) &&
        shouldDisableButton(row.patientTrialData),
      style: {
        backgroundColor: "#F0F1F3",
        color: "grey",
      },
    },
  ];

  const patientListColumns = [
    {
      name: "Patients Name",
      selector: (row) => (
        <div className="flex items-center">
          {Array.isArray(row.patientTrialData) &&
            shouldDisableButton(row.patientTrialData) ? (
            <>
              <p>
                <span className="recruiter-intials-disabled">
                  {row.firstName?.charAt(0).toUpperCase()}
                  {row.lastName?.charAt(0).toUpperCase()}
                </span>
              </p>
              <p className="pl-1 whitespace-nowrap overflow-hidden text-ellipsis">
                <span className="block font-mont text-[14px] font-[600] whitespace-nowrap overflow-hidden text-ellipsis text-[#87909E]">
                  {row.firstName} {row.lastName}
                </span>
              </p>
            </>
          ) : (
            <>
              <p>
                <span className="recruiter-intials ">
                  {row.firstName?.charAt(0).toUpperCase()}
                  {row.lastName?.charAt(0).toUpperCase()}
                </span>
              </p>
              <p className="pl-1 whitespace-nowrap overflow-hidden text-ellipsis">
                <span className="block font-mont text-[14px] font-[600] whitespace-nowrap overflow-hidden text-ellipsis">
                  {row.firstName} {row.lastName}
                </span>
              </p>
            </>
          )}
          {row?.docuSignStatus === "recipient-completed" && (
            <>
              &nbsp;&nbsp;&nbsp;
              <svg
                width="26"
                height="26"
                viewBox="0 0 26 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.62006 3.30203C10.5905 0.232657 15.4095 0.232657 16.3799 3.30203C16.9005 4.94853 18.7745 5.92516 20.6145 5.50885C24.0445 4.73277 26.454 8.49989 23.9944 10.7932C22.675 12.0234 22.675 13.9766 23.9944 15.2068C26.454 17.5001 24.0445 21.2672 20.6145 20.4912C18.7745 20.0748 16.9005 21.0515 16.3799 22.698C15.4095 25.7673 10.5905 25.7673 9.62006 22.698C9.0995 21.0515 7.22549 20.0748 5.38552 20.4912C1.95548 21.2672 -0.454034 17.5001 2.00558 15.2068C3.32499 13.9766 3.32499 12.0234 2.00558 10.7932C-0.454033 8.49989 1.95548 4.73277 5.38552 5.50885C7.22549 5.92516 9.0995 4.94853 9.62006 3.30203Z"
                  fill="#16C47F"
                />
                <path
                  d="M17.7552 9.78078L13.6377 14.2408C12.7912 15.1577 12.368 15.6161 11.8112 15.6161C11.2544 15.6161 10.8312 15.1577 9.98468 14.2408L8.2448 12.3562M5.38552 5.50885C7.22549 5.92516 9.0995 4.94853 9.62006 3.30203C10.5905 0.232657 15.4095 0.232657 16.3799 3.30203C16.9005 4.94853 18.7745 5.92516 20.6145 5.50885C24.0445 4.73277 26.454 8.49989 23.9944 10.7932C22.675 12.0234 22.675 13.9766 23.9944 15.2068C26.454 17.5001 24.0445 21.2672 20.6145 20.4912C18.7745 20.0748 16.9005 21.0515 16.3799 22.698C15.4095 25.7673 10.5905 25.7673 9.62006 22.698C9.0995 21.0515 7.22549 20.0748 5.38552 20.4912C1.95548 21.2672 -0.454034 17.5001 2.00558 15.2068C3.32499 13.9766 3.32499 12.0234 2.00558 10.7932C-0.454033 8.49989 1.95548 4.73277 5.38552 5.50885Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </>
          )}

        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Patients Name"),
    },
    {
      name: "Address",
      selector: (row) => (
        <div
          className={`${Array.isArray(row.patientTrialData) &&
              shouldDisableButton(row.patientTrialData)
              ? "text-[#87909E] border-[#87909E]"
              : ""
            }`}
        >
          {row.address}
        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Address"),
    },
    {
      name: "Studies",
      selector: (row) => (
        <div>
          {row.patientTrialData.map((val) =>
            val.trialID ? (
              <div
                key={val.trialID}
                className={`p-2 ${val.trialName.length < 20 ? "w-fit" : "w-full pr-[10px]"
                  } ${val.isPatientDeleted === "0"
                    ? "text-[#15B392] border-[#15B392]"
                    : Array.isArray(row.patientTrialData) &&
                      shouldDisableButton(row.patientTrialData)
                      ? "text-[#87909E] border-[#87909E]"
                      : "text-[#87909E] border-[#87909E]"
                  }   border-[1px] rounded-full font-mont font-[500] text-[14px] mb-2 `}
                style={{
                  backgroundColor:
                    val.isPatientDeleted === "0"
                      ? "rgba(21, 170, 146, 0.1)"
                      : Array.isArray(row.patientTrialData) &&
                        shouldDisableButton(row.patientTrialData)
                        ? "rgb(135, 144, 158, 0.1)"
                        : "rgb(135, 144, 158, 0.1)",
                }}
              >
                {!isBigDesktop
                  ? trimWithEllipsis(val.trialName)
                  : trimWithEllipsis(val.trialName, 20)}
              </div>
            ) : (
              <></>
            )
          )}
        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Studies"),
      width: isBigDesktop ? "220px" : "200px",
    },
    {
      name: "Source",
      selector: (row) => (
        <div
          className={`
           ${Array.isArray(row.patientTrialData) &&
              shouldDisableButton(row.patientTrialData)
              ? "text-[#87909E]"
              : ""
            }
          `}
        >
          {row.source}
        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Source"),
    },
    {
      name: "Conversation",
      selector: (row) => (
        <div className="w-[110px] text-center">
          <button
            disabled={
              Array.isArray(row.patientTrialData) &&
              shouldDisableButton(row.patientTrialData)
            }
            className={`
           ${Array.isArray(row.patientTrialData) &&
                shouldDisableButton(row.patientTrialData)
                ? "text-[#87909E]"
                : "text-[#245CEA]"
              }
          `}
            onClick={() =>{
              const latestObj = getLatestMessage(row.patientTrialData)
              onChatClick(latestObj.patientTrialJuncID, row)
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="22"
              viewBox="0 0 20 22"
              fill="none"
            >
              <path
                d="M10 8.23486L6 8.23486C5.58579 8.23486 5.25 7.89908 5.25 7.48486C5.25 7.07065 5.58579 6.73486 6 6.73486L10 6.73486C10.4142 6.73486 10.75 7.07065 10.75 7.48486C10.75 7.89908 10.4142 8.23486 10 8.23486Z"
                fill="currentColor"
              />
              <path
                d="M5.25 11.4849C5.25 11.0707 5.58579 10.7349 6 10.7349H14C14.4142 10.7349 14.75 11.0707 14.75 11.4849C14.75 11.8991 14.4142 12.2349 14 12.2349H6C5.58579 12.2349 5.25 11.8991 5.25 11.4849Z"
                fill="currentColor"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.96644 0.734863H11.0336C12.4053 0.734859 13.4807 0.734855 14.3451 0.805475C15.2252 0.877386 15.9523 1.02624 16.6104 1.36158C17.6924 1.91285 18.572 2.79249 19.1233 3.87442C19.4586 4.53255 19.6075 5.25966 19.6794 6.1398C19.75 7.00414 19.75 8.07957 19.75 9.45128V12.2081C19.75 12.4645 19.75 12.6207 19.7436 12.7568C19.6033 15.7187 17.2338 18.0882 14.2719 18.2284C14.1358 18.2349 13.9639 18.2349 13.6788 18.2349H13.6504L13.5751 18.235C12.7079 18.2405 11.8632 18.5112 11.1543 19.0108L11.1051 19.0457L8.49441 20.9105C6.9899 21.9852 5.01288 20.4332 5.69954 18.7166C5.79183 18.4858 5.62191 18.2349 5.37341 18.2349H4.77166C2.27441 18.2349 0.25 16.2104 0.25 13.7132L0.25 9.4513C0.249995 8.07958 0.249992 7.00415 0.320612 6.1398C0.392522 5.25966 0.541379 4.53255 0.876713 3.87442C1.42798 2.79249 2.30762 1.91285 3.38955 1.36158C4.04769 1.02624 4.77479 0.877386 5.65494 0.805475C6.51929 0.734855 7.59472 0.734859 8.96644 0.734863ZM5.77708 2.30049C4.9897 2.36483 4.48197 2.48845 4.07054 2.69809C3.27085 3.10555 2.62068 3.75572 2.21322 4.5554C2.00359 4.96683 1.87996 5.47457 1.81563 6.26195C1.75058 7.05808 1.75 8.07236 1.75 9.48486V13.7132C1.75 15.382 3.10284 16.7349 4.77166 16.7349H5.37341C6.68311 16.7349 7.57867 18.0576 7.09226 19.2737C6.96197 19.5994 7.33709 19.8938 7.62255 19.6899L10.2333 17.8251L10.2902 17.7846C11.2493 17.1088 12.3922 16.7425 13.5655 16.735L13.6504 16.7349C13.9721 16.7349 14.1053 16.7346 14.201 16.7301C16.3902 16.6265 18.1416 14.8751 18.2452 12.6858C18.2498 12.5899 18.25 12.4719 18.25 12.1837V9.48486C18.25 8.07236 18.2494 7.05808 18.1844 6.26195C18.12 5.47457 17.9964 4.96683 17.7868 4.5554C17.3793 3.75571 16.7291 3.10555 15.9295 2.69809C15.518 2.48845 15.0103 2.36483 14.2229 2.30049C13.4268 2.23545 12.4125 2.23486 11 2.23486H9C7.58749 2.23486 6.57322 2.23545 5.77708 2.30049Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Conversation"),
      width: isConversationContactSelected ? "auto" : "160px",
    },
    {
      name: "Last Outreach",
      selector: (row) => (
        <div
          className={`
           ${Array.isArray(row.patientTrialData) &&
              shouldDisableButton(row.patientTrialData)
              ? "text-[#87909E]"
              : ""
            }
          `}
        >
          {row.lastOutreach
            ? convertDateLastContracted(convertTimeToLocation(row.lastOutreach))
            : ""}
        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Last Outreach"),
      width: isConversationContactSelected ? "auto" : "190px",
    },
    {
      name: "Contact",
      selector: (row) => (
        <div
          className={`
           ${Array.isArray(row.patientTrialData) &&
              shouldDisableButton(row.patientTrialData)
              ? "text-[#87909E]"
              : ""
            }
          `}
        >
          {formatPhoneNumber(row.phoneNumber)}
        </div>
      ),
      omit: _patientList.hiddenColumns.includes("Contact"),
      width: isConversationContactSelected ? "auto" : "172px",
    },
    {
      name: "",
      selector: (row) => (
        <div className="flex item-center justify-end w-full">
          <div>
            <button
              onClick={() => handleView(row)}
              disabled={
                Array.isArray(row.patientTrialData) &&
                shouldDisableButton(row.patientTrialData)
              }
              title="View"
              className="px-[20px]"
              onMouseEnter={() => setIsViewHovered(row.patientID)}
              onMouseLeave={() => setIsViewHovered(null)}
            >
              {isViewHovered === row.patientID ? (
                <IconViewDetailHovered
                  color={
                    Array.isArray(row.patientTrialData) &&
                      shouldDisableButton(row.patientTrialData)
                      ? "#87909E"
                      : ""
                  }
                />
              ) : (
                <IconViewDetail
                  color={
                    Array.isArray(row.patientTrialData) &&
                      shouldDisableButton(row.patientTrialData)
                      ? "#87909E"
                      : ""
                  }
                />
              )}
            </button>
            <button
              onClick={() => handleEdit(row.patientID, row)}
              disabled={
                Array.isArray(row.patientTrialData) &&
                shouldDisableButton(row.patientTrialData)
              }
              title="Edit"
              onMouseEnter={() => setIsEditHovered(row.patientID)}
              onMouseLeave={() => setIsEditHovered(null)}
            >
              {isEditHovered === row.patientID ? (
                <IconEditHovered
                  color={
                    Array.isArray(row.patientTrialData) &&
                      shouldDisableButton(row.patientTrialData)
                      ? "#87909E"
                      : ""
                  }
                />
              ) : (
                <IconEdit
                  color={
                    Array.isArray(row.patientTrialData) &&
                      shouldDisableButton(row.patientTrialData)
                      ? "#87909E"
                      : ""
                  }
                />
              )}
            </button>
          </div>
        </div>
      ),
      width: "150px",
    },
  ];

  const handleView = (row) => {
    const patient = getPatientById(_patientList.data, row.patientID);

    setPatientDetails({
      name: patient.firstName ? patient.firstName : "",
      firstName: patient.firstName ? patient.firstName : "",
      lastName: patient.lastName ? patient.lastName : "",
      email: patient.emailAddress ? patient.emailAddress : "",
      contact: patient.phoneNumber,
      dob: patient.dob ? patient.dob : "",
      age: patient.age ? patient.age : "",
      zip: patient.zip ? patient.zip : "",
      gender: patient.gender ? patient.gender : "",
      source: patient.source ? patient.source : "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (id) => {
    navigate("/edit-patient", { state: { id } });
  };

  function getNonDeletedObj(data) {
    const result =
      Array.isArray(data) &&
      data.find((item) => String(item.isPatientDeleted) === "0");

    return result;
  }

  const onChatClick = (id, rowdata) => {
    const firstNotDeleted = getNonDeletedObj(rowdata.patientTrialData);
    const routeId = firstNotDeleted.patientTrialJuncID;
    navigate(`/conversation-patients/${id}`);
  };

  const handleToggleColumn = (columnName) => {
    setHiddenColumns((prev) =>
      prev.includes(columnName)
        ? prev.filter((col) => col !== columnName)
        : [...prev, columnName]
    );
  };

  const showAllCol = (toggle) => {
    if (toggle) setHiddenColumns([]);
    else
      setHiddenColumns(
        patientListColumns.filter((x) => x.name != "").map((x) => x.name)
      );

    
  };

  const handlePageChange = (page) => {
    _setPatientList({ ..._patientList, currentPage: page });
  };

  const handlePerPageChange = (perPage) => {
    _setPatientList({ ..._patientList, rowsPerPage: perPage, currentPage: 0 });
  };

  useEffect(() => {
    fetchData();
    const params = new URLSearchParams();
    params.set("page", parseInt(_patientList.currentPage) + 1);
    params.set("per_page", _patientList.rowsPerPage);
    params.set("sorting", sortOrder);

    if (_patientList.filter) params.set("filter", _patientList.filter);
    if (_patientList.cities) params.set("cities", _patientList.cities);
    if (_patientList.states) params.set("states", _patientList.states);
    if (_patientList.trialnames)
      params.set("trialnames", _patientList.trialnames);

    navigate(`${pathname}?${params.toString()}`, { replace: true });
  }, [
    _patientList.currentPage,
    _patientList.rowsPerPage,
    _patientList.sorts,
    _patientList.filter,
    sortOrder,
    _patientList.cities,
    _patientList.states,
    _patientList.trialnames,
    _patientList.refreshKey,
  ]);

  useEffect(() => {
    if (!search && filterParam) {
      _setPatientList({
        ..._patientList,
        filter: null,
        currentPage: 0,
      });
    }
  }, [search]);

  const closeModel = () => {
    setIsOpen(false);
  };


  return (
    <>
      <Header
        headertitle="Patients"
        showClose={showClose}
        isSearch={true}
        search={search}
        setSearch={setSearch}
        onSearchClick={onSearchClick}
        onCloseClick={onCloseClick}
      />

      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div
        className="main-body sub_header px-8 py-4 flex justify-between"
        style={{ borderColor: "#C8D6E6" }}
      >
        <div className="flex justify-between">
          <div className="flex">
            <Link
              className="btn-primary primary_btn mr-[20px]"
              to="/add-patient"
            >
              {" "}
              <svg
                class="h-5 w-5 mr-2 text-white-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {" "}
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>{" "}
                <circle cx="8.5" cy="7" r="4"></circle>{" "}
                <line x1="20" y1="8" x2="20" y2="14"></line>{" "}
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
              Add Patient
            </Link>
            <div className="flex">
              <div className="flex justify-between items-center w-full relative title_color">
                <div className="m-auto">Sort by:</div>
                <select
                  onChange={(e) => handleSort(e.target.value)}
                  className="focus:outline-none font-[500] appearance-none pl-[10px] pr-[20px]"
                  value={sortOrder}
                >
                  <option
                    value="-lastOutreach"
                    className="px-4 py-2 hover:bg-gray-200 cursor-pointer text-gray-700"
                  >
                    Newest
                  </option>
                  <option
                    value="lastOutreach"
                    className="px-4 py-2 hover:bg-gray-200 cursor-pointer text-gray-700"
                  >
                    Oldest
                  </option>
                </select>
                <span className="transform -translate-y-1/2 pointer-events-none absolute mt-6 right-[-10px]">
                  <IconSortyBy />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-5">
          <CustomPagination
            rowsPerPage={_patientList.rowsPerPage}
            rowCount={_patientList.totalCount}
            onChangePage={handlePageChange}
            currentPage={_patientList.currentPage}
            handlePerPageChange={handlePerPageChange}
            isDisable={isLoading}
          />

          <div className="relative" ref={dropdownRefExport}>
            {/* Export button */}
            <button
              onClick={handleToggleDropdown}
              className="inline-flex items-center py-[10px] px-[20px] bg-white border border-[#245CEA] rounded-md font-semibold text-gray-800 shadow-sm hover:bg-gray-100 focus:outline-none focus:none focus:none focus:none"
            >
              <IconExportDownload />
              <span className="pl-[10px]">Export</span>
              <span className="ml-2 text-xs text-gray-500">
                <IconExportArrow />
              </span>
            </button>
            {/* Dropdown with buttons */}

            {isDropdownVisible && (
              <div
                className="p-dd flex flex-col text-left border-solid border bg-white border-[#D1D7DC] w-full rounded-lg absolute -left-5 mt-5"
                onClick={handleToggleDropdown}
              >
                <PdfExport
                  name="PDF"
                  icon={true}
                  class="bg-red"
                  tableData={_patientList.data}
                  columns={patientListColumns}
                  fileName="Patients-List"
                  classes={
                    "py-2.5 px-5 me-2 mb-0 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-none border border-none hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 mr-0 text-left"
                  }
                />
                <ExcelExport
                  name="EXCEL"
                  data={_patientList.data}
                  containerClass={"py-[1px]"}
                  fileName="Patients-List"
                  classes={
                    "py-2.5 px-5 me-2 mb-0 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-none border border-none hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 mr-0 text-left  w-full"
                  }
                />
              </div>
            )}
          </div>
          <PrimaryIconButton
            label="Filter"
            isClose={isOpen}
            onClick={toggleFilter}
          />
        </div>
      </div>

      {isLoading ? (
        <div class="spinnerwrapper">
          <div class="spinner"></div>
        </div>
      ) : (
        <div className="main-body">
          <div className="">
            {_patientList && _patientList.data && _patientList.data.length ? (
              <>
                <div
                  className={`mt-5 px-8 pb-8 pt-4 ${isOpen ? "w-[calc(100vw-630px)]" : "flex-1"
                    } `}
                >
                  <div className="h-[calc(100vh-250px)] tbl-patient-grid">
                    <DataTable
                      fixedHeader
                      fixedHeaderScrollHeight="calc(100vh - 250px)"
                      columns={patientListColumns}
                      data={_patientList.data}
                      sortIcon={<span>⇵</span>}
                      customStyles={customStyles}
                      conditionalRowStyles={conditionalRowStyles}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <NoRecord
                  classes={"pt-7 px-7"}
                  svg={PatientsNoRecord}
                  message={"No patients records available at the moment."}
                />
              </>
            )}

            {isOpen && (
              <>
                <div
                  className={`transition-all block fixed top-[162px] right-0 h-full border-l border-[#B2ACC9] bg-[#F5F5F5] z-10 ${isOpen ? "block" : "hidden"
                    }`}
                >
                  {isOpen && (
                    <>
                      <Filter
                        clearFilter={clearFilter}
                        toggleFilter={toggleFilter}
                        patientListColumns={patientListColumns}
                        hiddenColumns={hiddenColumns}
                        showAllCol={showAllCol}
                        handleToggleColumn={handleToggleColumn}
                        selectedValues={{
                          states: searchParams.get("states"),
                          cities: searchParams.get("cities"),
                          trialnames: searchParams.get("trialnames"),
                        }}
                        filterValues={(val) => {
                          if (
                            hiddenColumns.length <
                            patientListColumns.length - 2
                          ) {
                            _setPatientList({
                              ..._patientList,
                              cities: val.cities,
                              states: val.states,
                              trialnames: val.trialnames,
                              currentPage: 0,
                              hiddenColumns: hiddenColumns,
                            });

                            setIsOpen(false);
                          } else {
                            addToast(
                              "Please select at least two column.",
                              "error"
                            );
                          }
                        }}
                        hideLastCol={true}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <div>
            <ViewModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              title="Patient Details"
              details={patientDetails}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Patients;
